import React, { useEffect, useState } from 'react';
import { supportAPI } from '../services/api';
import { SectionHeader } from '../components/ui';

const Help: React.FC = () => {
  const [form, setForm] = useState({ title: '', description: '', category: 'general' });
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [myTickets, setMyTickets] = useState<any[]>([]);
  const [publishedTickets, setPublishedTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [mine, published] = await Promise.all([
        supportAPI.listMyTickets(),
        supportAPI.listPublishedTickets()
      ]);
      setMyTickets(mine.tickets || []);
      setPublishedTickets(published.tickets || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load help data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    try {
      setSubmitting(true);
      await supportAPI.createTicket({
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        screenshots,
      });
      setSuccess('Support ticket submitted successfully.');
      setForm({ title: '', description: '', category: 'general' });
      setScreenshots([]);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to submit ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawTicket = async (ticket: any) => {
    setError(null);
    setSuccess(null);
    if (!ticket || ticket.status !== 'open') return;
    const confirmed = window.confirm(`Withdraw ticket ${ticket.ticket_number || ticket.id}?`);
    if (!confirmed) return;
    try {
      await supportAPI.withdrawTicket(ticket.id);
      setSuccess(`Ticket ${ticket.ticket_number || ticket.id} withdrawn.`);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to withdraw ticket.');
    }
  };

  if (loading) {
    return (
      <div className="container center">
        <div className="card">Loading help center...</div>
      </div>
    );
  }

  return (
    <div className="container portal-v3-page">
      <SectionHeader
        eyebrow="Help center"
        title="Find answers and contact support"
        description="Raise support tickets and review published guidance from admins."
      />

      {error && <div className="alert error alert--stacked">{error}</div>}
      {success && <div className="alert success alert--stacked">{success}</div>}

      <div className="card shadow help-card">
        <h3 className="h3">Create Support Ticket</h3>
        <form onSubmit={handleSubmit} className="help-form">
          <input
            className="input"
            placeholder="Ticket title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
          <select
            className="select"
            value={form.category}
            onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
          >
            <option value="general">General</option>
            <option value="technical">Technical</option>
            <option value="account">Account</option>
            <option value="billing">Billing</option>
            <option value="feature">Feature Request</option>
            <option value="other">Other</option>
          </select>
          <textarea
            className="input textarea"
            rows={5}
            placeholder="Describe your issue or question"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <div>
            <label className="label">Screenshots (optional, max 5)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 5) {
                  setError('You can upload up to 5 screenshots per ticket.');
                  return;
                }
                setScreenshots(files);
              }}
            />
            {screenshots.length > 0 && (
              <div className="muted help-form-files">
                {screenshots.map((file) => file.name).join(', ')}
              </div>
            )}
          </div>
          <div>
            <button className="btn" type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Ticket'}</button>
          </div>
        </form>
      </div>

      <div className="card shadow help-card">
        <h3 className="h3">My Tickets</h3>
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Ticket #</th>
                <th>Created</th>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Screenshots</th>
                <th>Admin Response</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {myTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td>{ticket.ticket_number || ticket.id}</td>
                  <td>{ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '—'}</td>
                  <td>{ticket.title}</td>
                  <td>{ticket.category}</td>
                  <td>{ticket.status}</td>
                  <td>
                    {(ticket.attachments || []).length === 0 ? '—' : (
                      <div className="help-ticket-attachments">
                        {(ticket.attachments || []).map((attachment: any) => (
                          <button
                            key={attachment.id}
                            className="btn btn-sm btn-outline"
                            onClick={async () => {
                              try {
                                const blob = await supportAPI.downloadAttachment(ticket.id, attachment.id);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = attachment.filename || `attachment_${attachment.id}`;
                                link.click();
                                window.URL.revokeObjectURL(url);
                              } catch {
                                setError('Failed to download screenshot.');
                              }
                            }}
                          >
                            {attachment.filename}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{ticket.admin_response || '—'}</td>
                  <td>
                    {ticket.status === 'open' ? (
                      <button className="btn btn-sm btn-outline" onClick={() => handleWithdrawTicket(ticket)}>
                        Withdraw
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {myTickets.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">No support tickets raised yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card shadow help-card">
        <h3 className="h3">Published Tickets (General Help)</h3>
        <div className="help-published-list">
          {publishedTickets.map((ticket) => (
            <div key={ticket.id} className="card help-published-item">
              <div className="help-published-head">
                <strong>{ticket.title}</strong>
                <span className="muted help-published-date">{ticket.published_at ? new Date(ticket.published_at).toLocaleDateString() : 'Published'}</span>
              </div>
              <p className="muted help-published-body">{ticket.description}</p>
              {ticket.admin_response && (
                <p className="help-published-admin"><strong>Admin:</strong> {ticket.admin_response}</p>
              )}
            </div>
          ))}
          {publishedTickets.length === 0 && (
            <div className="muted">No published help tickets yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
